import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { RepairHistoryResponseResult } from '../../../types/manage-my-repair-types';

export interface PanelBody {
  bodyDate: string;
  bodyTitle: string;
}

export interface HistoryPanels {
  body: PanelBody[];
  title: string;
  date: string;
}

@Component({
  selector: 'claims-athome-myaccount-appointments-history',
  templateUrl: './appointments-history.component.html',
  styleUrls: ['./appointments-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageMyRepairAppointmentsHistoryComponent {
  @Input() step = 3;
  @Input() mobileStep = 2;

  @Input() serviceIncluded = false;
  @Input() set appointmentsHistory(historyData: RepairHistoryResponseResult[]) {
    this.processAppointmentsHistory(historyData);
  }
  private readonly mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  private readonly limitSubject = new BehaviorSubject<number>(this.step);
  private readonly panelsSubject = new BehaviorSubject<HistoryPanels[]>([]);

  showAll = false;

  appointmentsHistoryPanels: HistoryPanels[] = [];

  readonly limitedPanels$: Observable<any[]> = combineLatest({
    limit: this.limitSubject,
    panels: this.panelsSubject,
  }).pipe(map(({ limit, panels }) => panels.slice(0, limit)));

  readonly moreOptionsAvailable$: Observable<boolean> = combineLatest({
    limit: this.limitSubject,
    panels: this.panelsSubject,
  }).pipe(map(({ limit, panels }) => limit < panels.length));

  vm$ = combineLatest({
    allPanels: this.panelsSubject,
    limitedPanels: this.limitedPanels$,
    moreOptionsAvailable: this.moreOptionsAvailable$,
  });

  private getAppointmentsHistoryPanels(
    historyData: RepairHistoryResponseResult[]
  ): HistoryPanels[] {
    return historyData?.map((item) => {
      const historyList = item?.repairData?.history || [];

      const bodyHistoryItems = historyList.slice(1).map((history) => {
        return { bodyDate: history.date, bodyTitle: history.status };
      });

      if (historyList.length > 0) {
        return {
          body: bodyHistoryItems,
          title: `${historyList[0].status}`,
          date: `${historyList[0].date}`,
        };
      }

      return null;
    });
  }

  private processAppointmentsHistory(
    historyData: RepairHistoryResponseResult[]
  ): void {
    this.appointmentsHistoryPanels =
      this.getAppointmentsHistoryPanels(historyData);
    this.panelsSubject.next(this.appointmentsHistoryPanels ?? []);
    const limit = this.detectMobileDevice() ? this.mobileStep : this.step;
    this.limitSubject.next(limit);
  }

  private detectMobileDevice(): boolean {
    return (
      this.mobileRegex.test(navigator.userAgent) || navigator.maxTouchPoints > 0
    );
  }

  showMoreToggle(): void {
    if (this.showAll) {
      const limit = this.detectMobileDevice() ? this.mobileStep : this.step;
      this.limitSubject.next(limit);
    } else {
      const allPanelsCount = this.panelsSubject.value.length;
      this.limitSubject.next(allPanelsCount);
    }
    this.showAll = !this.showAll;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
